import { get } from 'svelte/store';

import { apiClient } from '$lib/api/client';
import { customerList } from '$lib/api/lists';

/**
 * What an uploaded list is made of, over the three axes a customer buys on.
 *
 * # It is a second reading of the same rows
 *
 * The list is uploaded to be subtracted — these are the companies I already
 * have. The same rows also say who the customer sells to, and this is that
 * reading of them. Nothing new is uploaded and nothing is matched twice: the
 * profile is served from the list the exclusion already created.
 *
 * # Every share is a fraction, and the denominators differ
 *
 * A category's share of the corpus is routinely a tenth of a percent, so these
 * travel as fractions rather than whole percents — rounded to an integer they
 * would be zero, and the lift they are the denominator of infinite.
 *
 * The denominators are not all the same, and the field names say which is
 * which. A category's is the whole corpus. A size band's is the companies
 * whose headcount somebody measured, not all of them. A canton's is the
 * language regions the list actually serves — hence `share_of_served` — and
 * printing that against a national figure would be wrong by about a third.
 */

/** One category the list is characteristically made of. */
export interface ProfiledCategory {
  centroid_id: number;
  name: string;
  companies: number;
  share_of_list: number;
  share_of_corpus: number;
  lift: number;
}

/** One headcount band, in the list against the measured corpus. */
export interface SizeBand {
  band: string;
  companies: number;
  share_of_list: number;
  share_of_corpus: number;
  lift: number;
}

/**
 * How big the list's companies are.
 *
 * Every figure here is computed over the companies whose headcount somebody
 * established, which is not all of them — the service knows how many, and
 * deliberately does not send the number. See sizeProfileResponse in
 * internal/httpapi/listprofile.go for why, and for the bias it leaves
 * unstated: measured companies skew larger, so the median sits a little above
 * the list's true middle.
 *
 * `unreliable` means too few were measured to say anything at all. That is not
 * the same as the list being small, and not the same as its companies being
 * small.
 */
export interface SizeProfile {
  unreliable: boolean;
  bands: SizeBand[];
  quartile1: number;
  median: number;
  quartile3: number;
  corpus_quartile1: number;
  corpus_median: number;
  corpus_quartile3: number;
}

/** One language region's weight in the list and in the corpus. */
export interface LanguageShare {
  language: string;
  companies: number;
  share_of_list: number;
  share_of_corpus: number;
  lift: number;
}

/**
 * One canton and how many of the list's companies are in it.
 *
 * The map's input. Unlike ProfiledRegion this is not thresholded — every canton
 * the list touches appears, because a map of only the remarkable ones would be
 * blank for the ordinary customer.
 */
export interface CantonCount {
  canton: string;
  companies: number;
}

/** One canton the list is concentrated in, within the regions it serves. */
export interface ProfiledRegion {
  canton: string;
  companies: number;
  share_of_list: number;
  share_of_served: number;
  lift: number;
}

/**
 * Where the list's companies are.
 *
 * `languages` is the whole distribution including the regions the list has
 * nobody in, because for the rare customer who does cross the language line
 * that zero is the informative cell. `served` is the regions it is genuinely
 * present in, and it is what a selection should filter on.
 *
 * `uniform` says no canton stood out inside those regions — the common answer,
 * and it means region reduces to the language filter rather than meaning the
 * analysis found nothing.
 */
export interface RegionProfile {
  known: number;
  unreliable: boolean;
  uniform: boolean;
  languages: LanguageShare[];
  served: string[];
  cantons: ProfiledRegion[];

  /** Every canton with a company in it, largest first. Drawn as the map. */
  spread: CantonCount[];
}

/**
 * The whole profile.
 *
 * `covered` is how much of `categorised` the categories account for. It is
 * usually a fraction of it — ten categories over a quarter of the list is
 * typical — and a screen that shows the categories without it is telling the
 * customer those categories describe their whole business.
 *
 * `diffuse` says the list has no industry shape. It is a correct and common
 * answer, not a failure: a customer who sells to everyone with twenty staff
 * has a real ideal customer profile and it simply is not an industry.
 */
export interface ListProfile {
  rows: number;
  matched: number;
  categorised: number;
  covered: number;
  diffuse: boolean;
  categories: ProfiledCategory[];
  size: SizeProfile;
  region: RegionProfile;
}

/**
 * Ask what the current list is made of.
 *
 * Returns null when there is no list to profile. A list that is still being
 * matched answers 409 and the caller should keep watching the status it is
 * already watching — see `watchList` — because a half-matched list would
 * profile whichever part of the customer's file happened to be reached, in
 * file order, and answer confidently.
 */
export async function fetchListProfile(): Promise<ListProfile | null> {
  const list = get(customerList);
  if (!list) return null;

  const response = await apiClient.get<ListProfile>(
    `/deep-iq/lists/${encodeURIComponent(list.code)}/profile`
  );
  return withEveryList(response.data);
}

/**
 * Give every list on the profile a value, whatever the service sent.
 *
 * # Why this is at the boundary and not at each use
 *
 * A deployment older than a field sends no field, and `undefined.length`
 * throws — which does not fail politely. The whole step stops rendering, so the
 * customer loses the screen's own heading along with the chart that wanted the
 * data, and the console names a line that is only the first place the gap was
 * touched.
 *
 * That happened twice: once for `covered`, which the client could work out for
 * itself, and again for `region.spread`, which it cannot — per-canton counts
 * only the service knows. So guarding the arithmetic is not enough and the
 * defence belongs here, once, where the response enters: past this line every
 * list is a list and callers may iterate freely.
 *
 * Missing scalars are left alone. A number that is absent reads as zero in
 * arithmetic and shows as a wrong figure rather than a crash, which is a
 * different problem with a different fix — see IndustrySuggestion.covered.
 */
function withEveryList(profile: ListProfile): ListProfile {
  return {
    ...profile,
    categories: profile.categories ?? [],
    size: { ...profile.size, bands: profile.size?.bands ?? [] },
    region: {
      ...profile.region,
      languages: profile.region?.languages ?? [],
      served: profile.region?.served ?? [],
      cantons: profile.region?.cantons ?? [],
      spread: profile.region?.spread ?? []
    }
  };
}
